import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import { mergeRefs } from 'react-merge-refs';

import { usePopupContext } from '../popup';

import type { ForwardedRef, ReactElement } from 'react';

import type { PopupTriggerProps } from './popup-trigger.types';

import './popup-trigger.scss';

/**
 * The `PopupTrigger` component is used to trigger the opening and closing of the pop-up.
 * Requires a child element to be used as trigger. It typically contains a button or other interactive element that,
 * when clicked, toggles the visibility of the pop-up.
 *
 * ### Usage
 * Place the `PopupTrigger` component inside the `Popup` component to define the trigger of the popup.
 *
 * ```tsx
 * import { Popup, PopupTrigger, PopupContent } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomComponent() {
 *   return (
 *     <Popup>
 *       <PopupTrigger>Trigger</PopupTrigger>
 *       <PopupContent>Content</PopupContent>
 *     </Popup>
 *   );
 * }
 * ```
 */
const PopupTrigger = forwardRef(
  (props: PopupTriggerProps, forwardedRef: ForwardedRef<HTMLElement> | null): ReactElement => {
    const {
      children,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const popupContext = usePopupContext();
    if (popupContext == null) {
      throw new Error('Popup components must be wrapped in <Popup />');
    }

    const { interactions, refs } = popupContext;
    const ref = mergeRefs([refs.setReference, forwardedRef]);

    return (
      <Slot ref={ref} {...interactions.getReferenceProps(rest)}>
        {children}
      </Slot>
    );
  },
);

PopupTrigger.displayName = 'PopupTrigger';

export default PopupTrigger;
